import { createStore } from '@xstate/store';
import type { TaskCategory } from '../types';

// ============================================
// Task Categories Store - Verwaltet die Task-Kategorien
// ============================================

const CATEGORIES_LOCAL_STORAGE_KEY = 'roadmap_2026_task_categories';
const PERSISTENCE_DEBOUNCE_MS = 500;

export interface TaskCategoriesContext {
  categories: TaskCategory[];
}

// Lade Kategorien aus LocalStorage
function loadCategoriesFromLocalStorage(): TaskCategory[] {
  try {
    const saved = localStorage.getItem(CATEGORIES_LOCAL_STORAGE_KEY);
    if (!saved) return [];

    const parsed = JSON.parse(saved) as TaskCategory[];
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed;
  } catch {
    return [];
  }
}

// Speichere Kategorien in LocalStorage
function saveCategoriesToLocalStorage(categories: TaskCategory[]): void {
  localStorage.setItem(CATEGORIES_LOCAL_STORAGE_KEY, JSON.stringify(categories));
}

const initialCategories = loadCategoriesFromLocalStorage();

const initialContext: TaskCategoriesContext = {
  categories: initialCategories,
};

export const taskCategoriesStore = createStore({
  context: initialContext,

  on: {
    'category.add': (ctx, event: { category: TaskCategory }) => {
      if (ctx.categories.some(c => c.id === event.category.id)) {
        console.warn(`[taskCategoriesStore] Category with ID "${event.category.id}" already exists`);
        return ctx;
      }
      return {
        ...ctx,
        categories: [...ctx.categories, event.category],
      };
    },

    'category.update': (ctx, event: { categoryId: string; updates: Partial<Omit<TaskCategory, 'id'>> }) => {
      const index = ctx.categories.findIndex(c => c.id === event.categoryId);
      if (index === -1) {
        console.warn(`[taskCategoriesStore] Category with ID "${event.categoryId}" not found`);
        return ctx;
      }

      const updatedCategories = [...ctx.categories];
      updatedCategories[index] = {
        ...updatedCategories[index],
        ...event.updates,
      };

      return {
        ...ctx,
        categories: updatedCategories,
      };
    },

    'category.delete': (ctx, event: { categoryId: string }) => {
      const index = ctx.categories.findIndex(c => c.id === event.categoryId);
      if (index === -1) {
        console.warn(`[taskCategoriesStore] Category with ID "${event.categoryId}" not found`);
        return ctx;
      }
      return {
        ...ctx,
        categories: ctx.categories.filter(c => c.id !== event.categoryId),
      };
    },

    'categories.reset': () => {
      return {
        categories: [],
      };
    },

    'categories.import': (ctx, event: { categories: TaskCategory[] }) => {
      return {
        ...ctx,
        categories: event.categories,
      };
    },
  },
});

// ============================================
// Side Effects: Debounced Persistence
// ============================================

let saveTimeout: ReturnType<typeof setTimeout> | null = null;
let lastCategoriesHash = '';

function computeCategoriesHash(categories: TaskCategory[]): string {
  return categories.map(c => `${c.id}:${c.name}:${c.color}`).join('|');
}

taskCategoriesStore.subscribe((snapshot) => {
  const currentHash = computeCategoriesHash(snapshot.context.categories);

  if (currentHash === lastCategoriesHash) return;
  lastCategoriesHash = currentHash;

  if (saveTimeout) clearTimeout(saveTimeout);
  saveTimeout = setTimeout(() => {
    saveCategoriesToLocalStorage(snapshot.context.categories);
  }, PERSISTENCE_DEBOUNCE_MS);
});

// ============================================
// Helper-Funktionen
// ============================================

export function getCategoriesArray(): TaskCategory[] {
  return taskCategoriesStore.getSnapshot().context.categories;
}

export function getCategoryById(categoryId: string): TaskCategory | undefined {
  return taskCategoriesStore.getSnapshot().context.categories.find(c => c.id === categoryId);
}

// Generiert eine neue eindeutige Category-ID
export function generateCategoryId(baseName: string): string {
  const baseId = baseName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const existingCategories = getCategoriesArray();
  let id = baseId || 'new-category';
  let counter = 1;

  while (existingCategories.some(c => c.id === id)) {
    id = `${baseId}-${counter}`;
    counter++;
  }

  return id;
}

// Vordefinierte Farben fuer Kategorien
export const CATEGORY_COLORS = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#8b5cf6', // violet
  '#f97316', // orange
  '#ec4899', // pink
  '#14b8a6', // teal
  '#f59e0b', // amber
  '#ef4444', // red
  '#6366f1', // indigo
  '#84cc16', // lime
  '#a855f7', // purple
  '#06b6d4', // cyan
];

export type TaskCategoriesStore = typeof taskCategoriesStore;
